
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import Header from '@/components/Header';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Users, UserPlus, MessageCircle, MapPin, Trophy, Filter, Eye } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabaseUserService } from '@/services/supabaseUserService';
import { internalUserService } from '@/services/internalUserService';
import { teamService } from '@/services/teamService';
import { SecureProfilePhoto } from '@/components/profile/SecureProfilePhoto';
import { useToast } from '@/hooks/use-toast';
import { userConnectionsService } from '@/services/userConnectionsService';
import { playerConnectionsService } from '@/services/playerConnectionsService';

interface SearchableUser {
  id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  role: string;
  userType: 'supabase' | 'internal';
  profile_photo_url?: string;
  display_name?: string;
  sport?: string;
  location?: string;
}

interface SearchableTeam {
  team_id: number;
  team_name: string;
  team_description?: string;
  sport_type?: string;
  team_logo?: string;
  location?: string;
  age_group?: string;
  city?: string;
  state_province?: string;
  continent?: string;
  slug?: string;
}

const UserDiscovery = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser, profile: internalProfile } = useInternalAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchableUser[]>([]);
  const [teamResults, setTeamResults] = useState<SearchableTeam[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'teams'>('users');
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Filter states
  const [sportFilter, setSportFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [userConnections, setUserConnections] = useState<any[]>([]);
  
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { toast } = useToast();

  const user = supabaseUser || internalUser;
  const userProfile = profile || internalProfile;
  const isInternalUser = !!internalUser && !supabaseUser;
  const currentUserType = supabaseUser ? 'supabase' : 'internal';
  const currentUserRole = supabaseUser ? profile?.role : internalUser?.role;

  // Redirect if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Load connections and teams on mount (but NOT all users)
  useEffect(() => {
    const loadInitialData = async () => {
      if (initialLoadComplete) return;
      
      try {
        // Only load teams and connections initially - NOT all users
        const teams = await teamService.getTeams();
        
        const processedTeams = teams.map(team => ({
          team_id: team.team_id,
          team_name: team.team_name,
          team_description: team.team_description,
          sport_type: team.sport_type,
          team_logo: team.team_logo,
          location: team.location,
          age_group: team.age_group,
          city: team.city,
          state_province: team.state_province,
          continent: team.continent,
          slug: team.slug
        }));
        
        setTeamResults(processedTeams);
        await loadUserConnections();
        setInitialLoadComplete(true);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };

    loadInitialData();
  }, [user?.id, initialLoadComplete]);

  // Debounced typeahead search for users
  useEffect(() => {
    if (activeTab !== 'users') return;
    
    const cleanQuery = searchQuery.trim().replace(/^@+/, '');
    
    if (cleanQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, roleFilter]);

  // Load user connections
  const loadUserConnections = async () => {
    if (!user?.id) return;
    
    try {
      console.log('Loading user connections for:', user.id);
      const connections = await userConnectionsService.getConnections(user.id);
      console.log('User connections loaded:', connections);
      setUserConnections(connections || []);
    } catch (error) {
      console.error('Error loading user connections:', error);
      setUserConnections([]);
    }
  };

  // Check if user has existing connection
  const hasConnection = (targetUserId: string) => {
    return userConnections.some(conn => 
      conn.connected_user_id === targetUserId || conn.user_id === targetUserId
    );
  };

  const searchUsers = async () => {
    console.log('=== SEARCHING USERS ===');
    console.log('Search query:', searchQuery);
    
    setLoading(true);
    try {
      let results: SearchableUser[] = [];

      if (searchQuery.trim()) {
        // Clean the search query (remove @ if present)
        const cleanQuery = searchQuery.startsWith('@') ? searchQuery.slice(1) : searchQuery;
        
        // Search both user types
        const [supabaseUsers, internalUsers] = await Promise.all([
          supabaseUserService.searchUsers(cleanQuery),
          internalUserService.searchUsers(cleanQuery)
        ]);

        console.log('Search results:');
        console.log('- Supabase:', supabaseUsers.length);
        console.log('- Internal:', internalUsers.length);

        // Process Supabase users
        supabaseUsers.forEach(userData => {
          if (userData.id === user?.id) return; // Don't show current user
          
          const displayName = userData.username || 
                             `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 
                             'Anonymous User';
          
          results.push({
            id: userData.id,
            username: userData.username,
            first_name: userData.first_name,
            last_name: userData.last_name,
            role: userData.role,
            userType: 'supabase',
            profile_photo_url: userData.profile_photo_url,
            display_name: displayName
          });
        });

        // Process internal users
        internalUsers.forEach(userData => {
          if (userData.id === user?.id) return; // Don't show current user
          
          const displayName = userData.username || 
                             `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 
                             'Anonymous User';
          
          results.push({
            id: userData.id,
            username: userData.username,
            first_name: userData.first_name,
            last_name: userData.last_name,
            role: userData.role,
            userType: 'internal',
            profile_photo_url: userData.profile_photo_url,
            display_name: displayName
          });
        });
      } else {
        // No search query - show nothing
        setSearchResults([]);
        setLoading(false);
        return;
      }

      // Apply filters
      const filteredResults = results.filter(userData => {
        if (roleFilter !== 'all' && userData.role !== roleFilter) return false;
        return true;
      });

      console.log('Filtered results:', filteredResults.length);
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching users:', error);
      toast({
        title: "Error",
        description: "Failed to search users",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const searchTeams = async () => {
    console.log('=== SEARCHING TEAMS ===');
    console.log('Search query:', searchQuery);
    
    setLoading(true);
    try {
      let teams: SearchableTeam[] = [];
      
      if (searchQuery.trim() || sportFilter !== 'all' || locationFilter || ageGroupFilter !== 'all') {
        const allTeams = await teamService.getTeams();
        
        teams = allTeams.filter(team => {
          // Apply text search
          if (searchQuery && !team.team_name.toLowerCase().includes(searchQuery.toLowerCase())) {
            return false;
          }
          
          // Apply filters
          if (sportFilter !== 'all' && team.sport_type !== sportFilter) return false;
          if (ageGroupFilter !== 'all' && team.age_group !== ageGroupFilter) return false;
          if (locationFilter && !team.location?.toLowerCase().includes(locationFilter.toLowerCase()) && 
              !team.city?.toLowerCase().includes(locationFilter.toLowerCase()) &&
              !team.state_province?.toLowerCase().includes(locationFilter.toLowerCase()) &&
              !team.continent?.toLowerCase().includes(locationFilter.toLowerCase())) {
            return false;
          }
          
          return true;
        }).map(team => ({
          team_id: team.team_id,
          team_name: team.team_name,
          team_description: team.team_description,
          sport_type: team.sport_type,
          team_logo: team.team_logo,
          location: team.location,
          age_group: team.age_group,
          city: team.city,
          state_province: team.state_province,
          continent: team.continent
        }));
      } else {
        // Use existing team results
        teams = [...teamResults];
      }

      console.log('Team search results:', teams.length);
      setTeamResults(teams);
    } catch (error) {
      console.error('Error searching teams:', error);
      toast({
        title: "Error",
        description: "Failed to search teams",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (activeTab === 'users') {
      searchUsers();
    } else {
      searchTeams();
    }
  };

  const handleConnect = async (targetUser: SearchableUser, connectionType: 'current' | 'previous' | 'fan_of' = 'current') => {
    if (!user?.id) {
      console.log('No user ID available for connection');
      return;
    }

    try {
      console.log('=== CREATING CONNECTION ===');
      console.log('Current user:', user.id, currentUserType, currentUserRole);
      console.log('Target user:', targetUser.id, targetUser.userType, targetUser.role);
      console.log('Connection type:', connectionType);
      
      // Create general connection
      await userConnectionsService.createConnection(
        user.id,
        targetUser.id,
        'connect',
        currentUserType,
        targetUser.userType
      );

      // Create specific role-based connection for coaches
      if (currentUserRole === 'player' && targetUser.role === 'coach') {
        console.log('Creating coach-player connection');
        await playerConnectionsService.createCoachConnection(user.id, targetUser.id, connectionType);
      }

      toast({
        title: "Connection Request Sent",
        description: `${connectionType.replace('_', ' ')} connection request sent to ${targetUser.display_name}`,
      });

      // Reload connections to update UI
      await loadUserConnections();
    } catch (error) {
      console.error('Error creating connection:', error);
      toast({
        title: "Error",
        description: "Failed to send connection request",
        variant: "destructive"
      });
    }
  };

  const handleTeamConnect = async (team: SearchableTeam, connectionType: 'current' | 'previous' | 'applied_to') => {
    if (!user?.id) return;

    try {
      console.log('Creating team connection:', { team, connectionType });
      
      await playerConnectionsService.createTeamConnection(user.id, team.team_id, connectionType);

      toast({
        title: "Team Connection Created",
        description: `${connectionType.replace('_', ' ')} connection created with ${team.team_name}`,
      });
    } catch (error) {
      console.error('Error creating team connection:', error);
      toast({
        title: "Error",
        description: "Failed to create team connection",
        variant: "destructive"
      });
    }
  };

  const handleMessage = async (targetUser: SearchableUser) => {
    // TODO: Implement messaging functionality
    toast({
      title: "Message Feature",
      description: "Messaging feature will be implemented soon",
    });
  };

  const getRoleColor = (role: string) => {
    const colors = {
      'player': 'bg-blue-600/20 text-blue-400 border-blue-600/30',
      'coach': 'bg-green-600/20 text-green-400 border-green-600/30',
      'team-owner': 'bg-purple-600/20 text-purple-400 border-purple-600/30',
      'venue': 'bg-orange-600/20 text-orange-400 border-orange-600/30',
      'fan': 'bg-pink-600/20 text-pink-400 border-pink-600/30'
    };
    return colors[role] || colors['player'];
  };

  return (
    <div className="flex flex-col min-h-screen bg-black">
      {/* Show appropriate header based on device */}
      {isMobile ? <MobileHeader /> : <Header />}
      
      <main className={`flex-grow w-full ${
        isMobile 
          ? 'pt-16 mb-16 py-6 px-4' 
          : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'
      }`}>
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Discover Connections
            </h1>
            <p className="text-gray-400">
              Find teams, coaches, and other sports enthusiasts
            </p>
          </div>

          {/* Search and Filter Section */}
          <Card className="bg-[#0d1117] border border-gray-800">
            <CardContent className="pt-6">
              {/* Tab Selection */}
              <div className="flex gap-4 mb-6">
                <Button
                  onClick={() => setActiveTab('users')}
                  variant={activeTab === 'users' ? 'default' : 'outline'}
                  className={activeTab === 'users' ? 'bg-sports-green text-black' : 'border-gray-700 text-white hover:bg-gray-800'}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Find Users
                </Button>
                <Button
                  onClick={() => setActiveTab('teams')}
                  variant={activeTab === 'teams' ? 'default' : 'outline'}
                  className={activeTab === 'teams' ? 'bg-sports-green text-black' : 'border-gray-700 text-white hover:bg-gray-800'}
                >
                  <Trophy className="h-4 w-4 mr-2" />
                  Find Teams
                </Button>
              </div>

              {/* Search Bar */}
              <div className="flex gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder={`Search ${activeTab === 'users' ? 'users' : 'teams'} by name...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10 bg-gray-900 border-gray-700 text-white"
                  />
                </div>
                <Button 
                  onClick={handleSearch}
                  disabled={loading}
                  className="bg-sports-green text-black hover:bg-green-600"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Select value={sportFilter} onValueChange={setSportFilter}>
                  <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                    <SelectValue placeholder="Select Sport" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="all">All Sports</SelectItem>
                    <SelectItem value="basketball">Basketball</SelectItem>
                    <SelectItem value="football">Football</SelectItem>
                    <SelectItem value="soccer">Soccer</SelectItem>
                    <SelectItem value="baseball">Baseball</SelectItem>
                    <SelectItem value="tennis">Tennis</SelectItem>
                    <SelectItem value="volleyball">Volleyball</SelectItem>
                  </SelectContent>
                </Select>

                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Location (city, state, country)"
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="pl-10 bg-gray-900 border-gray-700 text-white"
                  />
                </div>

                <Select value={ageGroupFilter} onValueChange={setAgeGroupFilter}>
                  <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                    <SelectValue placeholder="Age Group" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="all">All Ages</SelectItem>
                    <SelectItem value="under-18">Under 18</SelectItem>
                    <SelectItem value="18-plus">18+</SelectItem>
                    <SelectItem value="21-plus">21+</SelectItem>
                    <SelectItem value="25-plus">25+</SelectItem>
                    <SelectItem value="30-plus">30+</SelectItem>
                  </SelectContent>
                </Select>

                {activeTab === 'users' && (
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                      <SelectValue placeholder="Role" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700">
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="player">Players</SelectItem>
                      <SelectItem value="coach">Coaches</SelectItem>
                      <SelectItem value="team-owner">Team Owners</SelectItem>
                      <SelectItem value="venue">Venues</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          <Card className="bg-[#0d1117] border border-gray-800">
            <CardHeader>
              <CardTitle className="text-sports-green flex items-center gap-2">
                <Filter className="h-5 w-5" />
                {activeTab === 'users' ? 'Users' : 'Teams'} Found ({activeTab === 'users' ? searchResults.length : teamResults.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-sports-green"></div>
                  <p className="mt-2 text-gray-400">Loading...</p>
                </div>
              ) : activeTab === 'users' && searchResults.length > 0 ? (
                <div className="space-y-4">
                  {searchResults.map((userData) => (
                    <div key={`${userData.userType}-${userData.id}`} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <SecureProfilePhoto
                          filePath={userData.profile_photo_url}
                          fallbackInitials={userData.display_name?.[0] || 'U'}
                          size="sm"
                        />
                        <div>
                          <p className="font-medium text-white">
                            {userData.display_name || 'Anonymous User'}
                          </p>
                          <div className="flex items-center gap-2">
                            <Badge className={`${getRoleColor(userData.role)} text-white text-xs`}>
                              {userData.role}
                            </Badge>
                            {currentUserRole === 'admin' && (
                              <Badge variant="outline" className="text-gray-400 border-gray-600 text-xs">
                                {userData.userType === 'internal' ? 'New School' : 'Old School'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {/* Check if user already has connection - if so, show "Connected" status */}
                        {hasConnection(userData.id) ? (
                          <Button
                            size="sm"
                            disabled
                            className="border-gray-500 text-gray-500 bg-transparent cursor-not-allowed"
                            variant="outline"
                          >
                            Connected
                          </Button>
                        ) : (
                          <>
                            {/* Show role-specific options for coaches ONLY if no existing connection */}
                            {userData.role === 'coach' && currentUserRole === 'player' ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleConnect(userData, 'current')}
                                  className="border-sports-green text-sports-green hover:bg-sports-green/10 bg-transparent"
                                  variant="outline"
                                >
                                  Current Coach
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleConnect(userData, 'previous')}
                                  className="border-blue-500 text-blue-500 hover:bg-blue-500/10 bg-transparent"
                                  variant="outline"
                                >
                                  Previous Coach
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleConnect(userData, 'fan_of')}
                                  className="border-red-500 text-red-500 hover:bg-red-500/10 bg-transparent"
                                  variant="outline"
                                >
                                  Fan Of
                                </Button>
                              </>
                            ) : (
                              /* Show generic Connect button for all other users */
                              <Button
                                size="sm"
                                onClick={() => handleConnect(userData)}
                                className="border-sports-green text-sports-green hover:bg-sports-green/10 bg-transparent"
                                variant="outline"
                              >
                                <UserPlus className="h-3 w-3 mr-1" />
                                Connect
                              </Button>
                            )}
                          </>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handleMessage(userData)}
                          className="bg-blue-500 text-white hover:bg-blue-600"
                        >
                          <MessageCircle className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : activeTab === 'teams' && teamResults.length > 0 ? (
                <div className="space-y-4">
                  {teamResults.map((team) => (
                    <div key={team.team_id} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center">
                          {team.team_logo ? (
                            <img src={team.team_logo} alt={team.team_name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <span className="text-lg font-bold text-sports-green">{team.team_name[0]}</span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-white">{team.team_name}</p>
                          <div className="flex items-center gap-2 text-sm">
                            {team.sport_type && (
                              <Badge className="bg-purple-600/20 text-purple-400 border-purple-600/30 text-xs">
                                {team.sport_type}
                              </Badge>
                            )}
                            {team.age_group && (
                              <Badge variant="outline" className="text-gray-400 border-gray-600 text-xs">
                                {team.age_group}
                              </Badge>
                            )}
                            {team.location && (
                              <span className="text-gray-400 text-xs">{team.location}</span>
                            )}
                          </div>
                          {team.team_description && (
                            <p className="text-xs text-gray-500 mt-1">{team.team_description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => navigate(`/teams/${team.slug || team.team_name.toLowerCase().replace(/\s+/g, '-')}`)}
                          className="border-gray-500 text-gray-300 hover:bg-gray-700 bg-transparent"
                          variant="outline"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Button>
                        {(currentUserRole === 'player' || currentUserRole === 'coach') ? (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleTeamConnect(team, 'current')}
                              className="border-sports-green text-sports-green hover:bg-sports-green/10 bg-transparent"
                              variant="outline"
                            >
                              Current Team
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleTeamConnect(team, 'previous')}
                              className="border-blue-500 text-blue-500 hover:bg-blue-500/10 bg-transparent"
                              variant="outline"
                            >
                              Previous Team
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleTeamConnect(team, 'current')}
                            className="border-sports-green text-sports-green hover:bg-sports-green/10 bg-transparent"
                            variant="outline"
                          >
                            <UserPlus className="h-3 w-3 mr-1" />
                            Follow Team
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Search className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">
                    {activeTab === 'users' && searchQuery.trim().length < 2
                      ? 'Type a username or name above to find people to connect with'
                      : `No ${activeTab} found matching your search`
                    }
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      
      {!isMobile && <Footer />}
      <MobileBottomNav />
    </div>
  );
};

export default UserDiscovery;
